import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const AdminBlogPage = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    image: "",
    category: "Maintenance Tips",
    tags: "",
    published: false
  });

  const categories = [
    "Maintenance Tips",
    "Performance",
    "Product Guides",
    "Industry News"
  ];

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/posts`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === "title" && !editingPost ? { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') } : {})
    }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      content: "",
      excerpt: "",
      image: "",
      category: "Maintenance Tips",
      tags: "",
      published: false
    });
    setEditingPost(null);
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt,
      image: post.image,
      category: post.category,
      tags: post.tags.join(", "),
      published: post.published
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const postData = {
      ...formData,
      tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
    };

    try {
      if (editingPost) {
        await axios.put(
          `${API}/blog/posts/${editingPost.post_id}`,
          postData,
          {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Post updated successfully!");
      } else {
        await axios.post(
          `${API}/blog/posts`,
          postData,
          {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Post created successfully!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error(error.response?.data?.detail || "Failed to save post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      await axios.delete(`${API}/blog/posts/${postId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Post deleted successfully!");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    }
  };

  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="admin-blog-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-primary font-medium uppercase tracking-wider">Admin Panel</span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mt-2" data-testid="page-title">
                Manage Blog Posts
              </h1>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-post-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white font-heading text-xl uppercase">
                    {editingPost ? "Edit Post" : "Create New Post"}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="post-form">
                  <div>
                    <Label htmlFor="title" className="text-zinc-400">Title</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      required
                      data-testid="post-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="slug" className="text-zinc-400">Slug</Label>
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      required
                      data-testid="post-slug-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="excerpt" className="text-zinc-400">Excerpt</Label>
                    <Textarea
                      id="excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleChange}
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      rows={2}
                      required
                      data-testid="post-excerpt-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="content" className="text-zinc-400">Content (HTML)</Label>
                    <Textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleChange}
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white font-mono text-sm"
                      rows={10}
                      required
                      data-testid="post-content-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="image" className="text-zinc-400">Image URL</Label>
                    <Input
                      id="image"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      required
                      data-testid="post-image-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category" className="text-zinc-400">Category</Label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="w-full mt-1 bg-zinc-950 border border-zinc-700 text-white rounded px-3 py-2"
                        data-testid="post-category-select"
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="tags" className="text-zinc-400">Tags (comma-separated)</Label>
                      <Input
                        id="tags"
                        name="tags"
                        value={formData.tags}
                        onChange={handleChange}
                        placeholder="tag1, tag2, tag3"
                        className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                        data-testid="post-tags-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      id="published"
                      checked={formData.published}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, published: checked }))}
                      data-testid="post-published-switch"
                    />
                    <Label htmlFor="published" className="text-zinc-400">Published</Label>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="border-zinc-700 text-zinc-400"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary"
                      data-testid="save-post-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        editingPost ? "Update Post" : "Create Post"
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-zinc-900 h-24 skeleton rounded"></div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-zinc-900 border border-zinc-800 rounded">
            <p className="text-zinc-400 text-lg mb-4">No blog posts yet.</p>
            <Button onClick={() => setDialogOpen(true)} className="btn-primary">
              Create Your First Post
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="posts-list">
            {posts.map((post) => (
              <div
                key={post.post_id}
                className="bg-zinc-900 border border-zinc-800 p-6 flex items-center justify-between"
                data-testid={`admin-post-${post.post_id}`}
              >
                <div className="flex items-center gap-4">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div>
                    <h3 className="text-white font-medium">{post.title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-zinc-400">
                      <span>{post.category}</span>
                      <span>•</span>
                      <span>{post.views} views</span>
                      <span>•</span>
                      <span className={post.published ? "text-green-500" : "text-zinc-500"}>
                        {post.published ? (
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Published</span>
                        ) : (
                          <span className="flex items-center gap-1"><EyeOff className="w-3 h-3" /> Draft</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(post)}
                    className="border-zinc-700 text-zinc-400 hover:text-white"
                    data-testid={`edit-${post.post_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(post.post_id)}
                    className="border-zinc-700 text-secondary hover:bg-secondary hover:text-white"
                    data-testid={`delete-${post.post_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogPage;
